import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Key, User, Envelope, Copy, Eye, EyeSlash, IdentificationCard, CreditCard, Lightning, TrendUp } from '@phosphor-icons/react';

export function Account() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!user) return null;

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(user.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-foreground mb-2">Account Settings</h1>
          <p className="text-muted-foreground">
            Manage your account information and API access
          </p>
        </div>

        <div className="space-y-6">
          {/* Subscription Plan */}
          <Card className="p-6">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-accent" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3>Subscription Plan</h3>
                  <Badge variant={user.tier === 'free' ? 'secondary' : 'default'}>
                    {user.tier === 'free' ? 'Free' : user.tier === 'professional' ? 'Professional' : 'Enterprise'}
                  </Badge>
                </div>
                <p className="text-muted-foreground">
                  {user.tier === 'free' 
                    ? 'Limited to public data analyses only'
                    : 'Full access to private data uploads and unlimited public analyses'
                  }
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-muted-foreground mb-1">Private Data Upload</p>
                <p className="text-foreground">
                  {user.tier === 'free' ? (
                    <span className="text-destructive">Not Available</span>
                  ) : (
                    <span className="text-teal-600 flex items-center gap-2">
                      <Lightning className="w-4 h-4" />
                      Unlimited
                    </span>
                  )}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-muted-foreground mb-1">Public Data Analyses</p>
                <p className="text-foreground">
                  {user.tier === 'free' ? (
                    `${user.publicAnalysesUsed} / ${user.publicAnalysesLimit} used`
                  ) : (
                    <span className="text-teal-600 flex items-center gap-2">
                      <TrendUp className="w-4 h-4" />
                      Unlimited
                    </span>
                  )}
                </p>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-muted-foreground mb-1">API Access</p>
                <p className="text-foreground">
                  {user.tier === 'free' ? 'Coming Soon' : 'Enabled'}
                </p>
              </div>
            </div>

            {user.tier === 'free' && (
              <div className="p-4 border border-border rounded-lg mb-4">
                <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-3">
                  Upgrade to Professional
                </p>
                <ul className="space-y-1.5 text-sm text-muted-foreground mb-4">
                  <li>Private data uploads and confidential analysis</li>
                  <li>Unlimited public dataset analyses</li>
                  <li>Priority processing queue</li>
                  <li>API access for automation</li>
                  <li>Extended data retention</li>
                </ul>
                <p className="text-sm text-foreground">
                  <span className="font-semibold">$49/month</span>
                  <span className="text-muted-foreground ml-2">or $490/year — save 17%</span>
                </p>
              </div>
            )}

            <div className="flex gap-3">
              {user.tier === 'free' ? (
                <Button variant="default" onClick={() => navigate('/checkout?plan=professional')}>
                  Upgrade to Professional
                </Button>
              ) : (
                <>
                  <Button variant="secondary">
                    Manage Subscription
                  </Button>
                  <Button variant="ghost">
                    View Billing History
                  </Button>
                </>
              )}
            </div>
          </Card>

          {/* Profile Information */}
          <Card className="p-6">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="mb-1">Profile Information</h3>
                <p className="text-muted-foreground">
                  Your account details and contact information
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-muted-foreground mb-2">Full Name</label>
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <User className="w-5 h-5 text-muted-foreground" />
                  <span className="text-foreground">{user.name}</span>
                </div>
              </div>

              <div>
                <label className="block text-muted-foreground mb-2">Email Address</label>
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Envelope className="w-5 h-5 text-muted-foreground" />
                  <span className="text-foreground">{user.email}</span>
                </div>
              </div>

              <div>
                <label className="block text-muted-foreground mb-2">User ID</label>
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <IdentificationCard className="w-5 h-5 text-muted-foreground" />
                  <span className="text-foreground font-mono text-sm">{user.id}</span>
                </div>
              </div>

              <div>
                <label className="block text-muted-foreground mb-2">Account Status</label>
                <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg">
                  <div className="w-2 h-2 bg-teal-500 rounded-full" />
                  <span className="text-teal-900">Active</span>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <Button variant="secondary">
                Edit Profile
              </Button>
            </div>
          </Card>

          {/* API Access */}
          <Card className="p-6">
            <div className="flex items-start gap-3 mb-6">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <Key className="w-5 h-5 text-teal-700" />
              </div>
              <div className="flex-1">
                <h3 className="mb-1">API Access Key</h3>
                <p className="text-muted-foreground">
                  Use this key to access CellReveal programmatically (coming soon)
                </p>
              </div>
            </div>

            <div className="bg-muted border border-border rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <code className="text-foreground break-all">
                  {apiKeyVisible ? user.apiKey : '••••••••••••••••••••'}
                </code>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => setApiKeyVisible(!apiKeyVisible)}
                    className="p-2 hover:bg-muted/80 rounded-lg transition-colors"
                    title={apiKeyVisible ? 'Hide API key' : 'Show API key'}
                  >
                    {apiKeyVisible ? (
                      <EyeSlash className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <Eye className="w-5 h-5 text-muted-foreground" />
                    )}
                  </button>
                  <button
                    onClick={handleCopyApiKey}
                    className="flex items-center gap-1.5 px-2 py-1.5 hover:bg-muted/80 rounded-lg transition-colors"
                    title="Copy API key"
                  >
                    <Copy className="w-4 h-4 text-muted-foreground" />
                    {copied && <span className="text-xs text-teal-600 font-mono">Copied</span>}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Keep this key secure.</span> Do not share it publicly or commit it to version control.
              </p>
            </div>

            <div className="mt-4 flex gap-3">
              <Button variant="secondary">
                Regenerate Key
              </Button>
              <Button variant="ghost">
                View API Documentation
              </Button>
            </div>
          </Card>

          {/* Usage Statistics */}
          <Card className="p-6">
            <h3 className="mb-6">Usage Statistics</h3>
            
            <div className="grid md:grid-cols-3 gap-px bg-border border border-border rounded-xl overflow-hidden">
              {[
                { label: 'Total analyses', value: '17' },
                { label: 'This month', value: '4' },
                { label: 'Success rate', value: '83.7%' },
              ].map(({ label, value }) => (
                <div key={label} className="p-5 bg-background">
                  <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">{label}</p>
                  <p className="text-3xl font-bold text-teal-600 font-display tabular-nums">{value}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Data Privacy Notice */}
          <div className="pt-6 border-t border-border">
            <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-3">
              Privacy & data security
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
              CellReveal is designed for research use only and is not intended for collecting personally identifiable information (PII) or handling sensitive patient data. All uploaded data is processed securely and deleted after analysis completion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
